import type {
  IDrawerFilters,
  IUnreadCountResponse,
  IPaginatedList,
  INotification,
  IGetDrawerNotificationsQuery,
  IGetDrawerFiltersQuery,
  IDrawerNotificationRaw,
  IUpdateNotificationReadStatusRequest,
  IBulkUpdateNotificationReadStatusRequest,
} from "Common/Interfaces";
import { SERVICE_TAG_TYPES } from "../../Constants";
import { messageCenterBaseApi } from "../../MessageCenterBaseAPI";
import { transformErrorResponse } from "../../Functions";

const CONTROLLER = "notifications";

export const notificationsApi = messageCenterBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUnreadCount: builder.query<IUnreadCountResponse, void>({
      query: () => `${CONTROLLER}/unread-count`,
      transformErrorResponse,
      providesTags: [SERVICE_TAG_TYPES.NOTIFICATION_UNREAD_COUNT],
    }),
    getDrawerFilters: builder.query<IDrawerFilters, IGetDrawerFiltersQuery>({
      query: ({ sessionStartTime }) => {
        const params = new URLSearchParams();
        if (sessionStartTime) params.set("sessionStartTime", sessionStartTime);
        return `${CONTROLLER}/drawer/filters?${params.toString()}`;
      },
      transformErrorResponse,
      providesTags: [SERVICE_TAG_TYPES.NOTIFICATION_DRAWER_FILTERS],
    }),
    getNotificationDetails: builder.query<INotification, number>({
      query: (id) => `${CONTROLLER}/${id}`,
      transformResponse: (raw: IDrawerNotificationRaw): INotification => ({
        id: String(raw.notificationId),
        applicationId: raw.applicationId,
        title: raw.title,
        message: raw.message,
        resources: raw.resources,
        applicationName: raw.application,
        isRead: raw.isRead,
        isDismissed: false,
        createdAt: raw.createdDate,
        deepLink: raw.links?.[0]?.url,
      }),
      transformErrorResponse,
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(messageCenterBaseApi.util.invalidateTags([SERVICE_TAG_TYPES.NOTIFICATION_DRAWER_FILTERS]));
        } catch {
          // do not invalidate on error
        }
      },
    }),
    getDrawerNotifications: builder.query<IPaginatedList<INotification>, IGetDrawerNotificationsQuery>({
      query: ({ offset, limit, search, applicationIds, sessionStartTime, isRead, createdDateFrom, createdDateTo }) => {
        const params = new URLSearchParams();
        params.set("offset", String(offset));
        params.set("limit", String(limit));
        if (search) params.set("searchText", search);
        if (applicationIds) params.set("applicationIds", applicationIds);
        if (sessionStartTime) params.set("sessionStartTime", sessionStartTime);
        if (isRead !== undefined) params.set("isRead", String(isRead));
        if (createdDateFrom) params.set("createdDateFrom", createdDateFrom.toISOString());
        if (createdDateTo) params.set("createdDateTo", createdDateTo.second(59).millisecond(999).toISOString());
        return `${CONTROLLER}/drawer?${params.toString()}`;
      },
      transformResponse: (raw: IPaginatedList<IDrawerNotificationRaw>): IPaginatedList<INotification> => ({
        ...raw,
        data: raw.data.map((item) => ({
          id: String(item.notificationId),
          applicationId: item.applicationId,
          title: item.title,
          message: item.message,
          resources: item.resources,
          applicationName: item.application,
          isRead: item.isRead,
          isDismissed: item.isDismissed,
          createdAt: item.createdDate,
          deepLink: item.links?.[0]?.url,
        })),
      }),
      transformErrorResponse,
      providesTags: [SERVICE_TAG_TYPES.NOTIFICATIONS],
    }),
    dismissNotification: builder.mutation<void, string>({
      query: (notificationId) => ({
        url: `${CONTROLLER}/${notificationId}/dismiss`,
        method: "PUT",
      }),
      transformErrorResponse,
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              SERVICE_TAG_TYPES.NOTIFICATION_UNREAD_COUNT,
              SERVICE_TAG_TYPES.NOTIFICATIONS,
              SERVICE_TAG_TYPES.NOTIFICATION_DRAWER_FILTERS,
            ],
    }),
    dismissAllNotifications: builder.mutation<void, void>({
      query: () => ({
        url: `${CONTROLLER}/dismiss`,
        method: "PUT",
      }),
      transformErrorResponse,
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              SERVICE_TAG_TYPES.NOTIFICATION_UNREAD_COUNT,
              SERVICE_TAG_TYPES.NOTIFICATIONS,
              SERVICE_TAG_TYPES.NOTIFICATION_DRAWER_FILTERS,
            ],
    }),
    updateNotificationReadStatus: builder.mutation<void, IUpdateNotificationReadStatusRequest>({
      query: ({ id, isRead }) => ({
        url: `${CONTROLLER}/${id}`,
        method: "PATCH",
        body: { isRead },
      }),
      transformErrorResponse,
      invalidatesTags: (_result, error) =>
        error ? [] : [SERVICE_TAG_TYPES.NOTIFICATION_UNREAD_COUNT, SERVICE_TAG_TYPES.NOTIFICATIONS],
    }),
    bulkUpdateNotificationReadStatus: builder.mutation<void, IBulkUpdateNotificationReadStatusRequest>({
      query: ({ ids, isRead }) => ({
        url: `${CONTROLLER}/read-status`,
        method: "PATCH",
        body: { notificationIds: ids.map(Number), isRead },
      }),
      transformErrorResponse,
      invalidatesTags: (_result, error) =>
        error ? [] : [SERVICE_TAG_TYPES.NOTIFICATION_UNREAD_COUNT, SERVICE_TAG_TYPES.NOTIFICATIONS],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUnreadCountQuery,
  useGetNotificationDetailsQuery,
  useLazyGetNotificationDetailsQuery,
  useGetDrawerFiltersQuery,
  useGetDrawerNotificationsQuery,
  useDismissNotificationMutation,
  useDismissAllNotificationsMutation,
  useUpdateNotificationReadStatusMutation,
  useBulkUpdateNotificationReadStatusMutation,
} = notificationsApi;
